import React, { useState, useContext, useEffect, useRef } from 'react';
import _ from 'lodash';
import {
  Button,
  Link,
  SkeletonText,
  FormLabel,
  Tabs,
  Tab,
  InlineLoading,
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Modal
} from 'carbon-components-react';
import {
  Add16,
  ArrowLeft16,
  Edit16,
  CheckmarkOutline16,
  CloseOutline16,
  Error16
} from '@carbon/icons-react';
import { useEmptyCallback } from 'utilities/hooks.util';
import Util from 'utilities/helper.util';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import {
  getOrganisationDetailStart,
  removeOrganisationTransactionLimitsStart,
  suspendOrganisationStart
} from '../../localStore/actions';
import OrganisationUpdateLogs from '../updateLogs';
import TransactionLimitsList from '../transactionLimitsList';
import { LimitTooltip, TransactionLimitTooltip } from '../limitTooltip';

const OrganisationsDetail: React.FC<Props> = ({
  selectedId,
  onBack,
  onOpenNewForm,
  onOpenTransactionLimitsForm,
  onRefreshTransactionLimits,
  refreshTransactionLimitsCounter,
  setEditData
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastLoginTime, setLastLoginTime] = useState<any>(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [disableSuspendOrgButton, setDisableSuspendOrgButton] =
    useState<boolean>(false);
  const [showConfirmationSuspendOrgModal, setShowConfirmationSuspendOrgModal] =
    useState<boolean>(false);
  const [pendingRemoveRestrictionLimitId, setPendingRemoveRestrictionLimitId] =
    useState<number>(0);
  const limitRemoved = useRef<boolean>(false);

  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const {
    organisationDetail,
    getOrgDetailStatus,
    updateMultiOrgsStatus,
    removeOrgTransactionLimitsStatus,
    reloadTrigger,
    suspendOrgStatus
  } = localStore;

  const getOrgTotalUsers = (org: any) => {
    const externalUsers = org.Projects.reduce(
      (accumulator: any[], project: any) =>
        _.unionBy(accumulator, project.Users, 'id'),
      []
    );

    return org.Users.length + externalUsers.length;
  };

  const getOrgDetailsData = useEmptyCallback((selectedId) => {
    localDispatch(getOrganisationDetailStart(selectedId));
  });

  useEffect(() => {
    getOrgDetailsData(selectedId);
  }, [reloadTrigger, selectedId, getOrgDetailsData]);

  useEffect(() => {
    setIsLoading(
      getOrgDetailStatus === 'fetching' || updateMultiOrgsStatus === 'updating'
    );
  }, [updateMultiOrgsStatus, getOrgDetailStatus]);

  useEffect(() => {
    if (organisationDetail) {
      const lastLoginArray = [];
      if (organisationDetail?.Users.length > 0) {
        for (const user of organisationDetail.Users) {
          const lastLogin = user.last_login || 0;
          lastLoginArray.push(lastLogin);
        }
      }
      if (lastLoginArray.length > 0) {
        lastLoginArray.sort(function (a: any, b: any) {
          const c: any = new Date(a);
          const d: any = new Date(b);
          return d - c;
        });
      }
      setLastLoginTime(lastLoginArray[0]);
    }
  }, [organisationDetail]);

  useEffect(() => {
    if (suspendOrgStatus !== 'suspending') {
      setDisableSuspendOrgButton(false);
    }
  }, [suspendOrgStatus]);

  useEffect(() => {
    if (
      limitRemoved.current &&
      removeOrgTransactionLimitsStatus === 'initial'
    ) {
      limitRemoved.current = false;
      onRefreshTransactionLimits();
      setPendingRemoveRestrictionLimitId(0);
    }

    if (
      removeOrgTransactionLimitsStatus === 'success' ||
      removeOrgTransactionLimitsStatus === 'failed'
    ) {
      limitRemoved.current = true;
    }
  }, [removeOrgTransactionLimitsStatus, onRefreshTransactionLimits]);

  const activeStatus = (
    <div className="org-status active">
      <div className="icon-container">
        <CheckmarkOutline16 />
      </div>
      <div>Active</div>
    </div>
  );

  const inactiveStatus = (
    <div className="org-status inactive">
      <div className="icon-container">
        <CloseOutline16 />
      </div>
      <div>Inactive</div>
    </div>
  );

  const suspendedStatus = (
    <div className="org-status inactive">
      <div className="icon-container">
        <Error16 />
      </div>
      <div>Suspended</div>
    </div>
  );

  const handleEditOrganisationClick = () => {
    onOpenNewForm();
    if (organisationDetail) {
      setEditData(organisationDetail);
    }
  };

  const confirmSuspendOrgAction = () => {
    setShowConfirmationSuspendOrgModal(false);
    const data = {
      action: organisationDetail?.is_inactive ? 'unsuspend' : 'suspend'
    };
    setDisableSuspendOrgButton(true);
    localDispatch(suspendOrganisationStart({ id: selectedId, data }));
  };

  const confirmRemoveOrgTransactionLimitAction = () => {
    localDispatch(
      removeOrganisationTransactionLimitsStart({
        organisationId: selectedId,
        restrictionLimitId: pendingRemoveRestrictionLimitId
      })
    );
  };

  const handleTabChange = (selectedIndex: any) => {
    setSelectedTabIndex(selectedIndex);
  };

  return (
    <div
      id="SysAdminOrgDetail"
      data-testid="SysAdminOrgDetail"
      className="sys-admin-org-detail"
    >
      <Modal
        onRequestClose={() => {
          setShowConfirmationSuspendOrgModal(false);
        }}
        open={showConfirmationSuspendOrgModal}
        className={`sys-admin-org-detail-suspend-org-confirmation-modal`}
        modalHeading="Confirm action"
        size="sm"
        primaryButtonText={
          organisationDetail?.is_inactive ? 'Unsuspend' : 'Suspend'
        }
        onRequestSubmit={() => confirmSuspendOrgAction()}
        preventCloseOnClickOutside
      >
        {organisationDetail?.is_inactive
          ? `This will unsuspend the organisation '${organisationDetail?.organisation_name}', and existing users will be able to access SmarterWX Locate`
          : `This will suspend the organisation '${organisationDetail?.organisation_name}', and existing users will no longer be able to access SmarterWX Locate`}
      </Modal>
      <ComposedModal
        onClose={() =>
          removeOrgTransactionLimitsStatus === 'initial'
            ? setPendingRemoveRestrictionLimitId(0)
            : false
        }
        open={pendingRemoveRestrictionLimitId !== 0}
        className="sys-admin-org-detail-remove-transaction-limit-modal"
        size="sm"
        preventCloseOnClickOutside
      >
        <ModalHeader title="Confirm action" />
        <ModalBody>This will delete this transaction limit</ModalBody>
        <ModalFooter>
          <Button
            kind="secondary"
            onClick={() => confirmRemoveOrgTransactionLimitAction()}
          >
            {removeOrgTransactionLimitsStatus === 'initial' ? (
              'Delete'
            ) : (
              <InlineLoading
                description={
                  removeOrgTransactionLimitsStatus === 'removing'
                    ? 'Deleting'
                    : removeOrgTransactionLimitsStatus === 'success'
                    ? 'Deleted'
                    : 'Failed'
                }
                status={
                  removeOrgTransactionLimitsStatus === 'success'
                    ? 'finished'
                    : removeOrgTransactionLimitsStatus === 'failed'
                    ? 'error'
                    : 'active'
                }
              />
            )}
          </Button>
        </ModalFooter>
      </ComposedModal>
      <div className="left-container">
        <div className={`title-container`}>
          <Link className="action" onClick={() => onBack()}>
            <ArrowLeft16 />
            Back to list
          </Link>
          <div className="header">
            <div className="title">
              {!isLoading ? (
                organisationDetail?.organisation_name
              ) : (
                <SkeletonText />
              )}
            </div>
            <div className="action-button-container">
              <Button
                onClick={() => handleEditOrganisationClick()}
                disabled={isLoading}
                renderIcon={Edit16}
              >
                Edit organisation
              </Button>
              <Button
                onClick={() => setShowConfirmationSuspendOrgModal(true)}
                disabled={disableSuspendOrgButton || isLoading}
                renderIcon={Error16}
                kind="danger--tertiary"
              >
                {organisationDetail?.is_inactive
                  ? 'Unsuspend organisation'
                  : 'Suspend organisation'}
              </Button>
            </div>
          </div>
        </div>

        <div className="tab-container">
          <Tabs type="default">
            <Tab
              id="organisationDetail"
              label="Organisation detail"
              onClick={() => handleTabChange(0)}
            >
              <div className="content-section-container">
                <div className={`content-section`}>
                  <div className="content">
                    <div className="content-label">Organisation status</div>
                    <div>
                      {!isLoading ? (
                        organisationDetail?.is_inactive ? (
                          suspendedStatus
                        ) : (
                          activeStatus
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">Date created</div>
                    <div className="content-info">
                      {!isLoading ? (
                        Util.toLocalDateTimeFormat(
                          organisationDetail?.created_at
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">Master administrator</div>
                    <div className="content-info">
                      {!isLoading ? (
                        organisationDetail?.Admin ? (
                          `${organisationDetail.Admin.first_name || ''} ${
                            organisationDetail.Admin.last_name || '-'
                          }`
                        ) : (
                          '-'
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">
                      Master administrator email
                    </div>
                    <div
                      className={`content-info${
                        organisationDetail?.Admin ? ' is-email' : ''
                      }`}
                    >
                      {!isLoading ? (
                        organisationDetail?.Admin ? (
                          organisationDetail.Admin.reg_email
                        ) : (
                          '-'
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">Organisation group</div>
                    <div className="content-info">
                      {!isLoading ? (
                        organisationDetail?.OrganisationGroupSubscribers
                          ?.organisation_group_id === 1 ? (
                          'EA'
                        ) : (
                          'BYDA'
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">Last 30 days enquiries</div>
                    <div className="content-info">
                      {!isLoading ? (
                        organisationDetail?.last_30_days_enquiries
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                </div>

                <div className={`content-section`}>
                  <div className="content">
                    <div className="content-label">BYDA premium features</div>
                    {!isLoading ? (
                      <>
                        <div>
                          {organisationDetail?.has_create_enquiry
                            ? activeStatus
                            : inactiveStatus}
                        </div>
                        {organisationDetail?.has_create_enquiry && (
                          <div className="content-caption">
                            Enquiries can now be lodged using SmarterWX Locate
                          </div>
                        )}
                      </>
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                  <div className="content">
                    <div className="content-label">Subscription start date</div>
                    <div className="content-info">
                      {!isLoading ? (
                        organisationDetail?.OrganisationGroupSubscribers
                          ?.subscription_start ? (
                          Util.getNewDateWithTimezone(
                            organisationDetail.OrganisationGroupSubscribers
                              .subscription_start,
                            'Australia/Melbourne'
                          ).format('DD/MM/YYYY')
                        ) : (
                          '-'
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">Subscription end date</div>
                    <div className="content-info">
                      {!isLoading ? (
                        organisationDetail?.OrganisationGroupSubscribers
                          ?.contract_renewal ? (
                          Util.getNewDateWithTimezone(
                            organisationDetail.OrganisationGroupSubscribers
                              .contract_renewal,
                            'Australia/Melbourne'
                          ).format('DD/MM/YYYY')
                        ) : (
                          '-'
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">
                      Final contractual end date
                    </div>
                    <div className="content-info">
                      {!isLoading ? (
                        organisationDetail?.OrganisationGroupSubscribers
                          ?.subscription_end ? (
                          Util.getNewDateWithTimezone(
                            organisationDetail.OrganisationGroupSubscribers
                              .subscription_end,
                            'Australia/Melbourne'
                          ).format('DD/MM/YYYY')
                        ) : (
                          '-'
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                  <div className="content">
                    <div className="content-label">Last login</div>
                    <div className="content-info">
                      {!isLoading ? (
                        lastLoginTime ? (
                          Util.toLocalDateTimeFormat(lastLoginTime)
                        ) : (
                          '-'
                        )
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                    <div className="content-caption">
                      Last login for any user associated with this organisation
                    </div>
                  </div>
                </div>
              </div>
            </Tab>
            <Tab
              id="limitsTransactions"
              label="Limits & transactions"
              onClick={() => handleTabChange(1)}
              className="limits-transactions-tab"
            >
              <div className="content-section-container">
                <div className="limits-container">
                  <div className="limits-label">
                    <FormLabel>Limits</FormLabel>
                    <LimitTooltip align="start" direction="bottom" />
                  </div>
                  <div className="user-team-limits-section">
                    <div className="content">
                      <div className="content-label">
                        Number of teams (used/total)
                      </div>
                      <div className="content-info">
                        {!isLoading ? (
                          organisationDetail?.Restrictions.find(
                            (r: any) => r.restriction_type === 'ADD_TEAM'
                          )?.Limits[0]?.amount ? (
                            `${organisationDetail.Projects.length}/${
                              organisationDetail.Restrictions.find(
                                (r: any) => r.restriction_type === 'ADD_TEAM'
                              ).Limits[0].amount
                            }`
                          ) : (
                            '-'
                          )
                        ) : (
                          <SkeletonText />
                        )}
                      </div>
                    </div>
                    <div className="content">
                      <div className="content-label">
                        Number of users (used/total)
                      </div>
                      <div className="content-info">
                        {!isLoading ? (
                          organisationDetail?.Restrictions.find(
                            (r: any) => r.restriction_type === 'ADD_USER'
                          )?.Limits[0]?.amount ? (
                            `${getOrgTotalUsers(organisationDetail)}/${
                              organisationDetail.Restrictions.find(
                                (r: any) => r.restriction_type === 'ADD_USER'
                              ).Limits[0].amount
                            }`
                          ) : (
                            '-'
                          )
                        ) : (
                          <SkeletonText />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="limits-button">
                    <Button
                      onClick={() => handleEditOrganisationClick()}
                      size="small"
                      disabled={isLoading}
                      renderIcon={Edit16}
                      kind="tertiary"
                    >
                      Edit limits
                    </Button>
                  </div>
                </div>
                <div className="limits-container">
                  <div className="limits-label">
                    <FormLabel>Transactions limits</FormLabel>
                    <TransactionLimitTooltip align="start" direction="bottom" />
                  </div>
                  <TransactionLimitsList
                    organisationId={selectedId}
                    refreshCounter={refreshTransactionLimitsCounter}
                    setPendingRemoveRestrictionLimitId={
                      setPendingRemoveRestrictionLimitId
                    }
                  />
                  <div className="limits-button">
                    <Button
                      onClick={onOpenTransactionLimitsForm}
                      size="small"
                      disabled={isLoading}
                      renderIcon={Add16}
                      kind="tertiary"
                    >
                      Add transaction limit
                    </Button>
                  </div>
                </div>
              </div>
            </Tab>
            <Tab
              id="updateLogs"
              label="View logs"
              onClick={() => handleTabChange(2)}
              className="update-logs-tab"
            >
              {selectedTabIndex === 2 && (
                <OrganisationUpdateLogs
                  selectedId={selectedId}
                  selectedTabIndex={selectedTabIndex}
                  orgCreatedAt={organisationDetail?.created_at}
                />
              )}
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

type Props = {
  selectedId: number;
  onBack: () => void;
  onOpenNewForm: () => void;
  onOpenTransactionLimitsForm: () => void;
  onRefreshTransactionLimits: () => void;
  refreshTransactionLimitsCounter: number;
  setEditData: Function;
};

export default React.memo(OrganisationsDetail);
